import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import StoreX, { store } from '../../redux/oldStore';
import redux, { IState, ReduxMap } from '../../redux/redux';
import { ServerResponse } from '../../utils/Server';

type TabKey = 'website' | 'paper';

interface IWebsiteEntry {
  ProjectId: string;
  ProjectKey: string;
  Title: string;
  EntryUrl?: string;
  WebsiteUrl?: string;
}

interface IPaperEntry {
  ProjectId: string;
  ProjectKey: string;
  Title: string;
}

/**
 * NHD website / paper entry lists (replaces XJudging Websites/Paper and judgeWebsite/judgePaper.aspx).
 */
const NhdEntryListsPage = (props: IState) => {
  const initialTab = useMemo((): TabKey => {
    const q = new URLSearchParams(window.location.search);
    const t = (q.get('tab') || '').toLowerCase();
    return t === 'paper' ? 'paper' : 'website';
  }, []);

  const [tab, setTab] = useState<TabKey>(initialTab);
  const [loading, setLoading] = useState(true);
  const [websiteEntries, setWebsiteEntries] = useState<IWebsiteEntry[]>([]);
  const [paperEntries, setPaperEntries] = useState<IPaperEntry[]>([]);
  const [loadedWebsite, setLoadedWebsite] = useState(false);
  const [loadedPaper, setLoadedPaper] = useState(false);

  const selectTab = (next: TabKey) => {
    setTab(next);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', next);
    window.history.replaceState({}, '', url.toString());
  };

  const loadWebsites = () => {
    setLoading(true);
    store.server
      .postApi<ServerResponse<{ Entries: IWebsiteEntry[] }>>('../XJudging/WebsitesData', {})
      .then((x) => {
        if (x.Success) {
          setWebsiteEntries(x.Value?.Entries || []);
          setLoadedWebsite(true);
        } else {
          if (x.LoginRequired) redux.showLogin(true);
          toast.error(x.Message || 'Unable to load website entries.', { autoClose: false });
        }
      })
      .catch(() => toast.error('Unable to load website entries.', { autoClose: false }))
      .finally(() => setLoading(false));
  };

  const loadPapers = () => {
    setLoading(true);
    store.server
      .postApi<ServerResponse<{ Entries: IPaperEntry[] }>>('../XJudging/PapersData', {})
      .then((x) => {
        if (x.Success) {
          setPaperEntries(x.Value?.Entries || []);
          setLoadedPaper(true);
        } else {
          if (x.LoginRequired) redux.showLogin(true);
          toast.error(x.Message || 'Unable to load paper entries.', { autoClose: false });
        }
      })
      .catch(() => toast.error('Unable to load paper entries.', { autoClose: false }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (tab === 'website' && !loadedWebsite) loadWebsites();
    if (tab === 'paper' && !loadedPaper) loadPapers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, props.User]);

  const paperFileUrl = (projectKey: string) =>
    StoreX.BuildUrlWithFair(`../File/EntryFile/${projectKey}`);

  return (
    <div className="main-body-page grow" data-testid="nhd-entry-lists-page" style={{ minHeight: 120 }}>
      <h2>Entry Lists</h2>
      <ul className="nav nav-tabs" role="tablist">
        <li className={tab === 'website' ? 'active' : ''} role="presentation">
          <a
            href="#website"
            onClick={(e) => {
              e.preventDefault();
              selectTab('website');
            }}
          >
            Website Entries
          </a>
        </li>
        <li className={tab === 'paper' ? 'active' : ''} role="presentation">
          <a
            href="#paper"
            onClick={(e) => {
              e.preventDefault();
              selectTab('paper');
            }}
          >
            Paper Entries
          </a>
        </li>
      </ul>

      {loading && <p>Loading…</p>}

      {!loading && tab === 'website' && (
        <>
          {websiteEntries.length === 0 ? (
            <p className="text-muted">No website entries found.</p>
          ) : (
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Entry #</th>
                  <th>NHDWebCentral Site Key</th>
                  <th>Url</th>
                  <th className="text-right">Title</th>
                </tr>
              </thead>
              <tbody>
                {websiteEntries.map((row) => (
                  <tr key={row.ProjectKey || row.ProjectId}>
                    <td>{row.ProjectId}</td>
                    <td>{row.EntryUrl}</td>
                    <td>
                      {row.WebsiteUrl ? (
                        <a href={row.WebsiteUrl} target="_blank" rel="noopener noreferrer" title="Open website in new tab">
                          {row.WebsiteUrl}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="text-right">{row.Title}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {!loading && tab === 'paper' && (
        <>
          {paperEntries.length === 0 ? (
            <p className="text-muted">No paper entries found.</p>
          ) : (
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Entry #</th>
                  <th>File url</th>
                  <th className="text-right">Title</th>
                </tr>
              </thead>
              <tbody>
                {paperEntries.map((row) => (
                  <tr key={row.ProjectKey || row.ProjectId}>
                    <td>{row.ProjectId}</td>
                    <td>
                      {row.ProjectKey ? (
                        <a
                          href={paperFileUrl(row.ProjectKey)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open file in new tab"
                        >
                          <i className="fa fa-file-text-o" /> Open
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="text-right">{row.Title}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

export default ReduxMap(NhdEntryListsPage);
